import { useState } from "react";
import { useTranslation } from "react-i18next";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Box, Checkbox, FormControlLabel, InputAdornment, Stack, TextField, Typography } from "@mui/material";

import { formatDecimal } from "../../../format/formatNumber";
import { DataTable } from "../../../table/DataTable";
import { useClientFileContext } from "../../context/ClientFileContext";
import { ClaimEditDialog } from "../components/edit-claim/ClaimEditDialog";
import { useClaimsList } from "../hooks/useClaimsList";
import { ClaimsTableWidth } from "./ClaimListTab.utils";

const ROW_HEIGHT = 52;

export function ClaimListTab() {
  const { clientId } = useClientFileContext();
  const { t, i18n } = useTranslation();
  const format = (amount: number) => formatDecimal(amount, i18n.language);

  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);

  const {
    claims,
    columns,
    isLoading,
    footerSaldo,
    footerDisputed,
    searchText,
    setSearchText,
    showCompleted,
    setShowCompleted,
    showSrvRegisteredOnly,
    setShowSrvRegisteredOnly,
    isSelectable,
    rowSelectionModel,
    onRowSelectionModelChange,
  } = useClaimsList(clientId);

  return (
    <>
      <Stack gap={2} sx={{ height: "100%", bgcolor: "background.paper", p: 2 }} overflow="hidden">
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            size="small"
            placeholder={t("claims.toolbar.search")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { "aria-label": t("claims.toolbar.search") },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={showCompleted} onChange={(_, checked) => setShowCompleted(checked)} size="small" />
            }
            label={t("claims.toolbar.showCompleted")}
            sx={{ whiteSpace: "nowrap", ml: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showSrvRegisteredOnly}
                onChange={(_, checked) => setShowSrvRegisteredOnly(checked)}
                size="small"
              />
            }
            label={t("claims.toolbar.srvRegistered")}
            sx={{ whiteSpace: "nowrap", ml: 0 }}
          />
        </Box>

        {claims.length === 0 && !isLoading ? (
          <Typography color="text.secondary">{t("claims.empty")}</Typography>
        ) : (
          <Stack sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
            <DataTable
              data={claims}
              columns={columns}
              isLoading={isLoading}
              getRowId={(row) => row.id}
              onRowClick={(rowId) => setSelectedClaimId(rowId)}
              rowHeight={ROW_HEIGHT}
              checkboxSelection={isSelectable}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={onRowSelectionModelChange}
              paginationResetKey={`${searchText}|${showCompleted}|${showSrvRegisteredOnly}`}
              footerContent={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "10px",
                    height: ROW_HEIGHT,
                    bgcolor: "grey.50",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {isSelectable && <Box sx={{ width: ClaimsTableWidth.selection, flexShrink: 0 }} />}
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                    {t("claims.table.total")}
                  </Typography>
                  <Box sx={{ flex: 1, maxWidth: ClaimsTableWidth.caseNumber }} />
                  <Box sx={{ flex: 1, maxWidth: ClaimsTableWidth.legalBasis }} />
                  <Box sx={{ flex: 1, maxWidth: ClaimsTableWidth.srvOrderNumber }} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flex: 1, maxWidth: ClaimsTableWidth.currentSaldo, textAlign: "right" }}
                  >
                    {format(footerSaldo)} €
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flex: 1, maxWidth: ClaimsTableWidth.disputedAmount, textAlign: "right" }}
                    color={footerDisputed > 0 ? "warning.main" : "text.primary"}
                  >
                    {format(footerDisputed)} €
                  </Typography>
                </Box>
              }
            />
          </Stack>
        )}
      </Stack>

      {selectedClaimId && (
        <ClaimEditDialog clientId={clientId} claimId={selectedClaimId} onClose={() => setSelectedClaimId(null)} />
      )}
    </>
  );
}
